import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from "@mui/material";
import Fuse from "fuse.js";
import { useMemo, useState } from "react";
import { useApiQuery } from "../hooks";
import type { Test } from "../types";

type Props = {
  label?: string;
  placeholder?: string;
  value: string | null;
  onChange: (testId: string | null) => void;
  disabled?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  helperText?: string;
  error?: boolean;
  path?: string;
  appConfigId?: string;
};

export const TestSelect = ({
  label = "Select Test",
  placeholder = "Search and select a test...",
  value,
  onChange,
  disabled = false,
  size = "medium",
  fullWidth = true,
  helperText,
  error = false,
  path = "/tests",
  appConfigId,
}: Props) => {
  // Build the query path with app_config_id if provided
  const queryPath = useMemo(() => {
    let basePath = path;
    // Add limit for vibe-qa tests endpoint to get enough tests for selection
    if (path.includes("/vibe-qa/tests") && !path.includes("limit=")) {
      const separator = path.includes("?") ? "&" : "?";
      basePath = `${path}${separator}limit=500`;
    }
    if (!appConfigId) return basePath;
    const separator = basePath.includes("?") ? "&" : "?";
    return `${basePath}${separator}app_config_id=${appConfigId}`;
  }, [path, appConfigId]);

  // Handle both array and paginated responses
  const { data: rawData, isLoading: isLoadingTests } = useApiQuery<
    Test[] | { items: Test[]; total: number }
  >(queryPath);

  // Normalize the response to always be an array
  const tests = useMemo((): Test[] => {
    if (!rawData) return [];
    if (Array.isArray(rawData)) return rawData;
    return rawData.items ?? [];
  }, [rawData]);

  const [searchQuery, setSearchQuery] = useState("");

  // Create Fuse instance for fuzzy search
  const fuse = useMemo(() => {
    return new Fuse(tests ?? [], {
      keys: ["name", "id"],
      threshold: 0.3,
      includeScore: true,
    });
  }, [tests]);

  // Filter tests based on search query
  const filteredTests = useMemo(() => {
    if (tests.length === 0) return [];

    if (!searchQuery.trim()) {
      return tests;
    }

    return fuse
      .search(searchQuery, { limit: 20 })
      .map(({ item }) => item)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [tests, searchQuery, fuse]);

  // Find the currently selected test
  const selectedTest = useMemo(() => {
    if (!value || tests.length === 0) return null;
    return tests.find((test) => test.id === value) || null;
  }, [value, tests]);

  return (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || isLoadingTests}
      loading={isLoadingTests}
      options={filteredTests}
      getOptionLabel={(option) => option.name}
      getOptionKey={(option) => option.id}
      value={selectedTest}
      onChange={(_, newValue, reason) => {
        onChange(newValue?.id || null);
        if (reason === "clear") {
          setSearchQuery("");
        }
      }}
      onInputChange={(_, newInputValue) => {
        setSearchQuery(newInputValue);
      }}
      inputValue={searchQuery}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1.5,
              fontSize: "0.875rem",
              fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
              "&:hover fieldset": {
                borderColor: "primary.main",
              },
              "&.Mui-focused fieldset": {
                borderColor: "primary.main",
                borderWidth: "1.5px",
              },
            },
            "& .MuiInputBase-input": {
              padding: "8px 12px",
              fontSize: "0.875rem",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.875rem",
              fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
            },
            "& .MuiFormHelperText-root": {
              fontSize: "0.75rem",
              fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoadingTests ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
            <Typography
              variant="body2"
              fontWeight="medium"
              sx={{
                fontSize: "0.875rem",
                fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
              }}
            >
              {option.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontFamily: "Geist, -apple-system, BlinkMacSystemFont, sans-serif",
              }}
            >
              ID: {option.id}
            </Typography>
          </Box>
        </Box>
      )}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} label={option.name} size="small" {...tagProps} />;
        })
      }
      noOptionsText={searchQuery ? "No tests found matching your search" : "No tests available"}
      loadingText="Loading tests..."
      clearOnBlur={false}
      clearOnEscape={false}
      selectOnFocus={false}
      blurOnSelect={true}
      openOnFocus={true}
      filterOptions={(x) => x} // Disable built-in filtering since we use Fuse
      sx={{
        "& .MuiAutocomplete-input": {
          cursor: "pointer",
        },
      }}
    />
  );
};
